/**
 * Coverage count strip (spec §4.1) — narrative-denominator counts with
 * click-to-filter, plus the bulk "Suggest mappings" action (visible when ≥1
 * requirement is unmapped) and "Accept all" once suggestions are pending.
 */

import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { useCoverageStore } from '@/stores/coverage-store'
import {
  FILTERABLE_COVERAGE_STATUSES,
  type FilterableCoverageStatus,
} from '@/components/requirement-coverage/coverage-status'
import type { CoverageSummary } from '@/lib/api'
import { cn } from '@/lib/utils'

interface CoverageSummaryStripProps {
  bidId: string
  summary: CoverageSummary
  /** e.g. "16 of 53 require response coverage" — caller renders the lead text. */
  lead?: string
  /**
   * Narrower than `CoverageStatus` deliberately: the strip is the only control
   * for this filter, so it can only be handed a status it can draw a button
   * for. `mapped` is not one — see `FILTERABLE_COVERAGE_STATUSES`.
   */
  activeFilter: FilterableCoverageStatus | null
  onFilter: (status: FilterableCoverageStatus | null) => void
  readOnly?: boolean
}

const FILTER_LABELS: Record<
  FilterableCoverageStatus,
  { label: (n: number) => string; alarm?: boolean }
> = {
  unmapped: { label: (n) => `${n} unmapped`, alarm: true },
  drafting: { label: (n) => `${n} drafting` },
  in_review: { label: (n) => `${n} in review` },
  addressed: { label: (n) => `${n} addressed` },
}

function countFor(summary: CoverageSummary, status: FilterableCoverageStatus): number {
  switch (status) {
    case 'unmapped': return summary.unmapped
    case 'drafting': return summary.drafting
    case 'in_review': return summary.inReview
    case 'addressed': return summary.addressed
  }
}

export function CoverageSummaryStrip({
  bidId,
  summary,
  lead,
  activeFilter,
  onFilter,
  readOnly,
}: CoverageSummaryStripProps) {
  const isSuggesting = useCoverageStore((s) => s.isSuggesting)
  const suggestions = useCoverageStore((s) => s.suggestions)
  const suggest = useCoverageStore((s) => s.suggest)
  const acceptSuggestion = useCoverageStore((s) => s.acceptSuggestion)

  // An empty register has nothing to say — unless a filter is lit, in which
  // case the strip is the only way back and has to stay on screen.
  if (summary.total === 0 && !activeFilter) return null

  const handleAcceptAll = () => {
    void (async () => {
      for (const suggestion of [...suggestions]) {
        await acceptSuggestion(bidId, suggestion, {
          artifactName: suggestion.artifactName,
          sectionTitle: suggestion.sectionTitle,
        })
      }
    })()
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-meta">
      {lead && <span>{lead}</span>}
      {FILTERABLE_COVERAGE_STATUSES.map((status) => {
        const { label, alarm } = FILTER_LABELS[status]
        const count = countFor(summary, status)
        // A bucket that empties normally drops out of the strip. The lit one
        // never does: mapping the last unmapped requirement would otherwise
        // take the button away while its filter stayed applied, leaving an
        // empty table and no control to clear.
        if (count === 0 && activeFilter !== status) return null
        return (
          <button
            key={status}
            type="button"
            onClick={() => onFilter(activeFilter === status ? null : status)}
            // The lit state is otherwise carried only by a wash and a font
            // weight — invisible to a screen reader and to any honest test.
            aria-pressed={activeFilter === status}
            className={cn(
              'rounded-md px-1.5 py-0.5 transition-colors hover:bg-accent hover:text-foreground',
              alarm && count > 0 && 'text-[hsl(var(--status-warning))]',
              activeFilter === status && 'bg-accent font-medium text-foreground'
            )}
          >
            {label(count)}
          </button>
        )
      })}

      {!readOnly && summary.unmapped > 0 && suggestions.length === 0 && (
        <Button
          size="sm"
          className="h-7 gap-1.5 px-2.5 text-xs"
          disabled={isSuggesting}
          onClick={() => void suggest(bidId)}
        >
          {isSuggesting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <GradientSparkleIcon className="h-3.5 w-3.5" />
          )}
          {isSuggesting ? 'Suggesting…' : 'Suggest mappings'}
        </Button>
      )}

      {!readOnly && suggestions.length > 0 && (
        <Button size="sm" className="h-7 gap-1.5 px-2.5 text-xs" onClick={handleAcceptAll}>
          <GradientSparkleIcon className="h-3.5 w-3.5" />
          Accept all {suggestions.length}
        </Button>
      )}
    </div>
  )
}
